import _get from 'lodash/get';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { BlockHeader } from '../blockHeader/blockHeader.component';
import { getInitialStyle, defaultContext, getTabStyle } from './selectorHeader.style';
import { SELECTED_SIGNAL } from '../../../../signals';

/**
 * Tabs organize content across different screens, data sets, and other interactions.
 *
 * @version 1.1.0
 * @see https://zeroheight.com/726d5c282/p/69caab-tab--v110/b/363d5e
 */
export class SelectorHeader extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Container: {},
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._focusedTabIndex = 0;
    this._selectedTabIndex = 0;
    this._leftPadding = 0;
    this._rightPadding = 0;
    this._containerWidth = 0;
    this._style = defaultContext;
  }

  _detach() {
    this.tag('Container').onUpdate = null;
  }

  set items(value) {
    const tabStyle = getTabStyle(this._style.screenSize);
    const container = this.tag('Container');

    if (container.children.length === value.length && value.length) {
      container.children.forEach((child, index) => {
        child.item = value[index];
      });

      return;
    }

    container.children = value.map(item => ({
      type: BlockHeader,
      item,
      ...tabStyle,
    }));

    container.children[this._selectedTabIndex].isSelected = true;

    container.onUpdate = () => {
      this._containerWidth = (container.flex && container.flex.item && container.flex.item.w) || 0;
      this.goTo(this._focusedTabIndex, false);
    };
  }

  get selectedIndex() {
    return this._selectedTabIndex;
  }

  set selectedIndex(value) {
    this._selectedTabIndex = value;
    this.tag('Container').children.forEach((child, index) => {
      child.isSelected = this._selectedTabIndex === index;
    });
  }

  get focusedIndex() {
    return this._focusedTabIndex;
  }

  set focusedIndex(value) {
    this._focusedTabIndex = value;
  }

  get leftPadding() {
    return this._leftPadding;
  }

  set leftPadding(value) {
    this._leftPadding = value;
    this.goTo(this._focusedItemIndex);
  }

  get rightPadding() {
    return this._rightPadding;
  }

  set rightPadding(value) {
    this._rightPadding = value;
    this.goTo(this._focusedItemIndex);
  }

  _handleEnter() {
    if (this._focusedTabIndex === this._selectedTabIndex) {
      return false;
    }
    const container = this.tag('Container');

    if (container.children[this._selectedTabIndex]) {
      container.children[this._selectedTabIndex].isSelected = false;
    }

    if (container.children[this._focusedTabIndex]) {
      container.children[this._focusedTabIndex].isSelected = true;
    }

    this._selectedTabIndex = this._focusedTabIndex;
    this.signal(SELECTED_SIGNAL, this._selectedTabIndex);

    return true;
  }

  goTo(index = this._focusedTabIndex, animate = true) {
    const container = this.tag('Container');
    if (!container.children.length) {
      return;
    }

    const flexItem = _get(container, `flex._layout.items.${index}`);
    if (!flexItem) {
      return;
    }

    const totalWidth = container.flex.item.w || this._containerWidth;
    const maxOffset = Math.min(this.finalW - this._leftPadding - this._rightPadding, totalWidth) - totalWidth;
    const offset = -flexItem.x;
    const result = Math.max(Math.min(0, offset), maxOffset);
    if (animate) {
      container.setSmooth('x', result);
    } else {
      container.x = result;
    }

    this._focusedTabIndex = index;
  }

  _handleLeft() {
    if (this._focusedTabIndex > 0) {
      this.goTo(this._focusedTabIndex - 1);

      return true;
    }

    return false;
  }

  _handleRight() {
    if (this._focusedTabIndex < this.tag('Container').children.length - 1) {
      this.goTo(this._focusedTabIndex + 1);

      return true;
    }

    return false;
  }

  _captureKey() {
    this.tag('Container').onUpdate = null;

    return false;
  }

  _getFocused() {
    return this.tag('Container').children[this._focusedTabIndex];
  }
}
